/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {EditorState, Transaction, TextSelection} from 'prosemirror-state';
import {EditorView} from 'prosemirror-view';
import {
    chainCommands, createParagraphNear, liftEmptyBlock,
    newlineInCode, splitBlock
} from 'prosemirror-commands';
import {DOMParser} from 'prosemirror-model';
import MarkCommand from './commands/Mark';
import ListCommand from './commands/ListItem';
import LinkCommand, {LinkAttrs} from './commands/Link';
import HeadingCommand, {HeadingLevels} from './commands/Heading';
import ImageCommand, {ImageAttrs} from './commands/Image';
import TextColorCommand from './commands/TextColor';
import TextAlignCommand, {Align} from './commands/TextAlign';

const execMark = (name: string, toggle = false) => {
    return (state: EditorState, dispatch: (tr: Transaction) => void) => {
        const command = new MarkCommand(name);

        if (!toggle) {
            return command.apply()(state, dispatch);
        }

        return command.toggle()(state, dispatch);
    };
};

class EditorCommands {
    private view: EditorView;

    private state: EditorState;

    private tr: Transaction;

    /**
     * Default constructor.
     * 
     * @param view EditorView.
     */
    constructor(view: EditorView) {
        if (!view) {
            throw Error('FcAgEditor: Required view to initialize commands.');
        }

        this.view = view;
        this.state = view.state;
        this.tr = this.view.state.tr;
    }

    private applyTrx = (tr?: Transaction) => {
        this.state = this.state.apply(tr ?? this.tr);
        this.tr = this.state.tr;
        this.tr.setMeta('APPLIED_TRX', true);
    }

    private dispatch = (tr: Transaction): void => {
        this.applyTrx(tr);
    }

    /**
     * Execution function.
     * 
     * @returns Boolean.
     */
    exec(): boolean {
        // No changes applied
        if (!this.tr.getMeta('APPLIED_TRX')) {
            return false;
        }

        const forceEmit = !this.view.state.doc.eq(this.state.doc);
        this.view.updateState(this.state);

        const tr = this.tr
            .setMeta('FORCE_EMIT', forceEmit);

        this.view.dispatch(tr);
        return true;
    }

    /**
     * Focus command.
     * 
     * @returns EditorCommands.
     */
    focus(): this {
        this.view.focus();
        return this;
    }

    /**
     * Scroll into view.
     * 
     * @returns EditorCommands.
     */
    scrollIntoView(): this {
        this.tr.scrollIntoView();
        this.applyTrx();
        return this;
    }

    /**
     * Insert text.
     * 
     * @param text Text string.
     * @returns EditorCommands.
     */
    insertText(text: string): this {
        this.tr.insertText(text);
        this.applyTrx();
        return this;
    }

    /**
     * Insert new line.
     * 
     * @returns EditorCommands.
     */
    insertNewLine(): this {
        const newLineCommands = [newlineInCode, createParagraphNear, liftEmptyBlock, splitBlock];
        chainCommands(...newLineCommands)(this.state, this.dispatch);
        return this;
    }

    /**
     * Apply Mark.
     * 
     * @param name Command name.
     * @returns EditorCommands.
     */
    applyMark(name: string): this {
        execMark(name, false)(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle Mark.
     * 
     * @param name Command name.
     * @returns EditorCommands.
     */
    toggleMark(name: string): this {
        execMark(name, true)(this.state, this.dispatch);
        return this;
    }

    /**
     * Bold text.
     * 
     * @returns EditorCommands.
     */
    bold(): this {
        execMark('strong')(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle bold text.
     * 
     * @returns EditorCommands.
     */
    toggleBold(): this {
        execMark('strong', true)(this.state, this.dispatch);
        return this;
    }

    /**
     * Italics text.
     * 
     * @returns EditorCommands.
     */
    italics(): this {
        execMark('em')(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle Italics text.
     * 
     * @returns EditorCommands.
     */
    toggleItalics(): this {
        execMark('em', true)(this.state, this.dispatch);
        return this;
    }

    /**
     * Underline text.
     * 
     * @returns EditorCommands.
     */
    underline(): this {
        execMark('u')(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle underline text.
     * 
     * @returns EditorCommands.
     */
    toggleUnderline(): this {
        execMark('u', true)(this.state, this.dispatch);
        return this;
    }

    /**
     * Strike text.
     * 
     * @returns EditorCommands.
     */
    strike(): this {
        execMark('s')(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle strike text.
     * 
     * @returns EditorCommands.
     */
    toggleStrike(): this {
        execMark('s', true)(this.state, this.dispatch);
        return this;
    }

    /**
     * Wrap text in code tag.
     * 
     * @returns EditorCommands.
     */
    code(): this {
        execMark('code')(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle wrap text in code tag.
     * 
     * @returns EditorCommands.
     */
    toggleCode(): this {
        execMark('code', true)(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle ordered list.
     * 
     * @returns EditorCommands.
     */
    toggleOrderedList(): this {
        const command = new ListCommand(false);
        command.toggle()(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle bullet list.
     * 
     * @returns EditorCommands.
     */
    toggleBulletList(): this {
        const command = new ListCommand(true);
        command.toggle()(this.state, this.dispatch);
        return this;
    }

    /**
     * Toggle heading type of text.
     * 
     * @param level HeadingLevels.
     * @returns EditorCommands.
     */
    toggleHeading(level: HeadingLevels): this {
        const command = new HeadingCommand(level);
        command.toggle()(this.state, this.dispatch);
        return this;
    }

    /**
     * Insert text with link.
     * 
     * @param text Text string.
     * @param attrs Link Attrs.
     * @returns EditorCommands.
     */
    insertLink(text: string, attrs: LinkAttrs): this {
        const command = new LinkCommand();
        command.insert(text, attrs)(this.state, this.dispatch);
        return this;
    }

    /**
     * Update link text.
     * 
     * @param attrs Link attrs.
     * @returns EditorCommands.
     */
    updateLink(attrs: LinkAttrs): this {
        const command = new LinkCommand();
        command.update(attrs)(this.state, this.dispatch);
        return this;
    }

    /**
     * Insert image.
     * 
     * @param src Image url.
     * @param attrs Image attrs.
     * @returns EditorCommands.
     */
    insertImage(src: string, attrs: ImageAttrs = {}): this {
        const command = new ImageCommand();
        command.insert(src, attrs)(this.state, this.dispatch);
        return this;
    }

    /**
     * Change text color.
     * 
     * @param color Text color.
     * @returns EditorCommands.
     */
    textColor(color: string): this {
        const command = new TextColorCommand('text_color');
        command.apply({color})(this.state, this.dispatch);
        return this;
    }

    /**
     * Change background color.
     * 
     * @param color Background color.
     * @returns EditorCommands.
     */
    backgroundColor(color: string): this {
        const command = new TextColorCommand('text_background_color');
        command.apply({backgroundColor: color})(this.state, this.dispatch);
        return this;
    }

    /**
     * Remove text color.
     * 
     * @returns EditorCommands.
     */
    removeTextColor(): this {
        const command = new TextColorCommand('text_color');
        command.remove()(this.state, this.dispatch);
        return this;
    }

    /**
     * Remove background color.
     * 
     * @returns EditorCommands.
     */
    removeBackgroundColor(): this {
        const command = new TextColorCommand('text_background_color');
        command.remove()(this.state, this.dispatch);
        return this;
    }

    /**
     * Align text.
     * 
     * @param p Align type.
     * @returns EditorCommands.
     */
    align(p: Align): this {
        const command = new TextAlignCommand(p);
        command.toggle()(this.state, this.dispatch);
        return this;
    }

    /**
     * Insert html string.
     * 
     * @param html HTML string.
     * @returns EditorCommands.
     */
    insertHTML(html: string): this {
        const {selection, schema, tr} = this.state;
        const {from, to} = selection;

        const element = document.createElement('div');
        element.innerHTML = html.trim();
        const slice = DOMParser.fromSchema(schema).parseSlice(element);

        const transaction = tr.replaceRange(from, to, slice);
        this.applyTrx(transaction);

        return this;
    }

    /**
     * Support tester for selection.
     *
     * @param from FromSelection.
     * @param to ToSelection.
     */
    selectText(from: number, to: number): void {
        let doc: any = this.state.doc;
        let sel: any = new TextSelection(doc.resolve(from), doc.resolve(to));
        this.view.dispatch(this.tr.setSelection(sel));
    }
}

export default EditorCommands;
